import {
  getSinglesPairingPolicy,
  isSinglesFormat,
} from './competitiveMatchFormat.js';
import { getSinglesEloForComputation } from './playerElo.js';

function normalizeGender(g) {
  if (g == null) return null;
  return String(g).toLowerCase();
}

export function pairAllowedByPolicy(g1, g2, policy) {
  const a = normalizeGender(g1);
  const b = normalizeGender(g2);
  if (!a || !b) {
    return policy.allowUnbalancedSingles;
  }
  if (a === 'male' && b === 'male') return policy.allowLevelsSingles;
  if (a === 'female' && b === 'female') return policy.allowLevelsSingles;
  if (a !== b) return policy.allowUnbalancedSingles;
  return false;
}

function getSinglesElo(player) {
  return getSinglesEloForComputation(player);
}

function courtsWatchedForPair(playerId, metrics) {
  const m = metrics?.[playerId];
  if (!m) return 0;
  return m.courtsWatched ?? m.courts_watched ?? 0;
}

/**
 * Deterministic 1v1 matchmaking: minimize singles Elo gap, then wait fairness, then stable player-id order.
 * Ignores partnerships. Respects scheduled_game_id exclusion via caller's availablePlayers pool.
 *
 * @returns {{ courts: Array<{team1: string[], team2: string[]}>, waitingList: string[] }}
 */
export function getBestSinglesCourtAssignments(
  availablePlayers,
  numberOfCourts,
  _sessionState,
  playersById,
  _contextAware,
  _partnerships,
  playerWaitMetrics = {},
  _scheduledGames = [],
  gameTypeRestriction = 'all_types',
  _playerCategories = null,
  matchFormat = 'singles'
) {
  if (!isSinglesFormat(matchFormat)) {
    return { courts: [], waitingList: availablePlayers };
  }

  const policy = getSinglesPairingPolicy(gameTypeRestriction, matchFormat);

  const eligiblePlayers = availablePlayers.filter((playerId) => {
    const player = playersById[playerId];
    return player && !player.scheduled_game_id;
  });

  let remaining = [...eligiblePlayers];
  const courts = [];

  for (let courtIdx = 0; courtIdx < numberOfCourts; courtIdx++) {
    if (remaining.length < 2) break;

    const candidates = [];
    for (let i = 0; i < remaining.length; i++) {
      for (let j = i + 1; j < remaining.length; j++) {
        const id1 = remaining[i];
        const id2 = remaining[j];
        const p1 = playersById[id1];
        const p2 = playersById[id2];
        if (!p1 || !p2) continue;
        if (!pairAllowedByPolicy(p1.gender, p2.gender, policy)) continue;

        const e1 = getSinglesElo(p1);
        const e2 = getSinglesElo(p2);
        const eloDiff = Math.abs(e1 - e2);
        const w1 = courtsWatchedForPair(id1, playerWaitMetrics);
        const w2 = courtsWatchedForPair(id2, playerWaitMetrics);
        const waitSum = w1 + w2;
        const s1 = String(id1);
        const s2 = String(id2);
        const firstPlayer = s1 < s2 ? id1 : id2;
        const secondPlayer = s1 < s2 ? id2 : id1;
        candidates.push({
          firstPlayer,
          secondPlayer,
          eloDiff,
          waitSum,
          k1: String(firstPlayer),
          k2: String(secondPlayer),
        });
      }
    }

    if (candidates.length === 0) break;

    candidates.sort((x, y) => {
      if (x.eloDiff !== y.eloDiff) return x.eloDiff - y.eloDiff;
      if (x.waitSum !== y.waitSum) return x.waitSum - y.waitSum;
      if (x.k1 !== y.k1) return x.k1.localeCompare(y.k1);
      return x.k2.localeCompare(y.k2);
    });

    const best = candidates[0];
    courts.push({
      team1: [best.firstPlayer],
      team2: [best.secondPlayer],
    });
    remaining = remaining.filter(
      (id) => id !== best.firstPlayer && id !== best.secondPlayer
    );
  }

  return { courts, waitingList: remaining };
}
